import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { 
  Crown, MapPin, Phone, ArrowRight, Instagram, CheckCircle, 
  ChevronDown, Menu, X, Plus, Shield, Activity, Droplet, Target, Coffee, Wind, Fingerprint,
  Mail
} from 'lucide-react';

const PremiumGymLanding = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (page: string) => {
    setCurrentPage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageTransition: Variants = {
    initial: { opacity: 0, y: 30, scale: 0.98 },
    in: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, ease: "easeOut" as const } },
    out: { opacity: 0, y: -30, scale: 0.98, transition: { duration: 0.5, ease: "easeIn" as const } }
  };

  return (
    <div className="bg-zinc-950 text-zinc-300 min-h-screen font-sans selection:bg-amber-500 selection:text-zinc-950 overflow-x-hidden">
      
      {/* === SOFT DARK PREMIUM HEADER === */}
      <nav className={`fixed w-full z-[100] transition-all duration-700 ${scrolled ? 'bg-zinc-950/90 backdrop-blur-xl border-b border-zinc-800 py-4 shadow-2xl' : 'bg-gradient-to-b from-zinc-950/80 to-transparent py-8'}`}>
        <div className="max-w-[90rem] mx-auto px-6 lg:px-12 flex justify-between items-center">
          
          {/* Logo */}
          <div onClick={() => handleNavClick('home')} className="flex items-center gap-3 cursor-pointer group z-50">
            <div className="w-10 h-10 bg-zinc-900 border border-amber-500/30 flex items-center justify-center rounded-lg group-hover:bg-amber-500 transition-colors duration-500">
              <Crown size={22} className="text-amber-500 group-hover:text-zinc-950 transition-colors duration-500" />
            </div>
            <div className="flex flex-col">
              <span className="font-serif text-2xl text-white tracking-widest uppercase leading-none">Apex</span>
              <span className="font-sans text-[10px] text-amber-500 tracking-[0.3em] uppercase font-bold mt-1">Elite Club</span>
            </div>
          </div>
          
          {/* Desktop Mega Menu */}
          <div className="hidden lg:flex gap-8 font-sans uppercase text-[11px] font-bold tracking-[0.15em] items-center text-zinc-400">
            
            <button onClick={() => handleNavClick('home')} className={`transition-colors hover:text-white ${currentPage === 'home' ? 'text-amber-500' : ''}`}>Home</button>
            
            {/* Dropdown 1: The Club */}
            <div className="relative group py-6">
              <button onClick={() => handleNavClick('club')} className={`flex items-center gap-1 transition-colors hover:text-white ${currentPage === 'club' ? 'text-amber-500' : ''}`}>
                The Club <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-500"/>
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-64 bg-zinc-900 border border-zinc-800 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 flex flex-col shadow-2xl rounded-b-xl overflow-hidden">
                <div className="w-full h-1 bg-gradient-to-r from-amber-600 to-amber-400"></div>
                {['Our Heritage', 'Master Architects', 'Facility Design', 'Global Partners'].map((item) => (
                  <span key={item} onClick={() => handleNavClick('club')} className="px-6 py-4 border-b border-zinc-800/50 hover:bg-zinc-800 hover:text-amber-500 cursor-pointer text-[10px] font-bold tracking-widest uppercase transition-colors text-zinc-300">{item}</span>
                ))}
              </div>
            </div>

            {/* Dropdown 2: Disciplines */}
            <div className="relative group py-6">
              <button onClick={() => handleNavClick('disciplines')} className={`flex items-center gap-1 transition-colors hover:text-white ${currentPage === 'disciplines' ? 'text-amber-500' : ''}`}>
                Disciplines <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-500"/>
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-72 bg-zinc-900 border border-zinc-800 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 flex flex-col shadow-2xl rounded-b-xl overflow-hidden">
                <div className="w-full h-1 bg-gradient-to-r from-amber-600 to-amber-400"></div>
                {['Private Coaching', 'Group Masterclasses', 'Combat & Agility', 'Pilates Studio'].map((item) => (
                  <span key={item} onClick={() => handleNavClick('disciplines')} className="px-6 py-4 border-b border-zinc-800/50 hover:bg-zinc-800 hover:text-amber-500 cursor-pointer text-[10px] font-bold tracking-widest uppercase transition-colors text-zinc-300">{item}</span>
                ))}
              </div>
            </div>

            {/* Dropdown 3: Recovery */}
            <div className="relative group py-6">
              <button onClick={() => handleNavClick('recovery')} className={`flex items-center gap-1 transition-colors hover:text-white ${currentPage === 'recovery' ? 'text-amber-500' : ''}`}>
                Recovery <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-500"/>
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-72 bg-zinc-900 border border-zinc-800 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 flex flex-col shadow-2xl rounded-b-xl overflow-hidden">
                <div className="w-full h-1 bg-gradient-to-r from-amber-600 to-amber-400"></div>
                {['The Wellness Suite', 'Whole-Body Cryo', 'Sports Science Clinic', 'IV Hydration Lounge'].map((item) => (
                  <span key={item} onClick={() => handleNavClick('recovery')} className="px-6 py-4 border-b border-zinc-800/50 hover:bg-zinc-800 hover:text-amber-500 cursor-pointer text-[10px] font-bold tracking-widest uppercase transition-colors text-zinc-300">{item}</span>
                ))}
              </div>
            </div>

            {/* Dropdown 4: Memberships */}
            <div className="relative group py-6">
              <button onClick={() => handleNavClick('memberships')} className={`flex items-center gap-1 transition-colors hover:text-white ${currentPage === 'memberships' ? 'text-amber-500' : ''}`}>
                Privileges <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-500"/>
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-64 bg-zinc-900 border border-zinc-800 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 flex flex-col shadow-2xl rounded-b-xl overflow-hidden">
                <div className="w-full h-1 bg-gradient-to-r from-amber-600 to-amber-400"></div>
                {['Membership Tiers', 'Application Protocol', 'Corporate Access'].map((item) => (
                  <span key={item} onClick={() => handleNavClick('memberships')} className="px-6 py-4 border-b border-zinc-800/50 hover:bg-zinc-800 hover:text-amber-500 cursor-pointer text-[10px] font-bold tracking-widest uppercase transition-colors text-zinc-300">{item}</span>
                ))}
              </div>
            </div>

            <button onClick={() => handleNavClick('concierge')} className={`transition-colors hover:text-white ${currentPage === 'concierge' ? 'text-amber-500' : ''}`}>Concierge</button>
          </div>
          
          <button onClick={() => handleNavClick('concierge')} className="hidden lg:flex items-center gap-2 bg-amber-500 text-zinc-950 hover:bg-white uppercase text-[10px] tracking-[0.15em] font-black py-3 px-8 rounded-full transition-all duration-500 shadow-[0_0_20px_rgba(245,158,11,0.3)] z-50">
            Apply Now <ArrowRight size={14} />
          </button>

          {/* Mobile Menu Toggle */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden z-50 text-amber-500 bg-zinc-900 p-2 rounded-lg border border-zinc-800">
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Full Screen Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, x: '100%' }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: '100%' }} transition={{ type: 'tween', duration: 0.4 }} className="fixed inset-0 bg-zinc-950 z-40 flex flex-col justify-center px-10">
               {['home', 'club', 'disciplines', 'recovery', 'memberships', 'concierge'].map((item, i) => (
                 <motion.button initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} key={item} onClick={() => handleNavClick(item)} className="text-3xl font-serif text-left text-white hover:text-amber-500 uppercase tracking-widest py-5 border-b border-zinc-800">
                   {item}
                 </motion.button>
               ))}
               <button onClick={() => handleNavClick('concierge')} className="mt-10 bg-amber-500 text-zinc-950 px-8 py-4 uppercase tracking-widest font-black rounded-xl">Book a Tour</button>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Main Content Area */}
      <main className="flex-grow pt-24">
        <AnimatePresence mode="wait">
          <motion.div key={currentPage} initial="initial" animate="in" exit="out" variants={pageTransition}>
            {currentPage === 'home' && <HomeView setPage={handleNavClick} />}
            {currentPage === 'club' && <ClubView />}
            {currentPage === 'disciplines' && <DisciplinesView setPage={handleNavClick} />}
            {currentPage === 'recovery' && <RecoveryView setPage={handleNavClick} />}
            {currentPage === 'memberships' && <MembershipsView setPage={handleNavClick} />}
            {currentPage === 'concierge' && <ConciergeView />}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* === FOOTER === */}
      <footer className="bg-zinc-950 pt-32 pb-12 border-t border-zinc-900">
        <div className="max-w-[90rem] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-16 mb-24">
          <div className="md:col-span-5">
            <h2 className="font-serif text-3xl text-white tracking-widest uppercase mb-6 flex items-center gap-3">
               <Crown size={28} className="text-amber-500" />
               Apex <span className="text-amber-500 italic">Elite</span>
            </h2>
            <p className="text-zinc-500 font-light max-w-md leading-relaxed mb-8 text-sm">
              An exclusive sanctuary dedicated to the relentless pursuit of physical and mental mastery. Unparalleled privacy, unmatched precision, wrapped in vibrant luxury.
            </p>
            <div className="flex gap-4">
              <div className="text-zinc-400 hover:text-zinc-950 hover:bg-amber-500 transition-all cursor-pointer border border-zinc-800 bg-zinc-900 p-3 rounded-xl"><Instagram size={20} /></div>
            </div>
          </div>
          <div className="md:col-span-4">
             <h4 className="text-white font-sans text-xs tracking-[0.2em] uppercase mb-8 font-bold">The Protocol</h4>
             <ul className="space-y-4 text-sm font-light text-zinc-400">
               <li className="hover:text-amber-500 cursor-pointer transition-colors flex items-center gap-2" onClick={() => handleNavClick('club')}><ArrowRight size={14} className="text-amber-500"/> Our Philosophy</li>
               <li className="hover:text-amber-500 cursor-pointer transition-colors flex items-center gap-2" onClick={() => handleNavClick('disciplines')}><ArrowRight size={14} className="text-amber-500"/> Training Architecture</li>
               <li className="hover:text-amber-500 cursor-pointer transition-colors flex items-center gap-2" onClick={() => handleNavClick('recovery')}><ArrowRight size={14} className="text-amber-500"/> Recovery Spa</li>
               <li className="hover:text-amber-500 cursor-pointer transition-colors flex items-center gap-2" onClick={() => handleNavClick('memberships')}><ArrowRight size={14} className="text-amber-500"/> Privileges</li>
             </ul>
          </div>
          <div className="md:col-span-3">
             <h4 className="text-white font-sans text-xs tracking-[0.2em] uppercase mb-8 font-bold">Connect</h4>
             <ul className="space-y-4 text-sm font-light text-zinc-400">
               <li className="flex items-start gap-3"><MapPin size={18} className="text-amber-500 shrink-0"/> Level 4, The Opus Tower<br/>Golf Course Road, City</li>
               <li className="flex items-center gap-3 text-amber-500 mt-6"><Mail size={18}/> concierge@apexelite.demo</li>
               <li className="flex items-center gap-3 text-amber-500"><Phone size={18}/> +91 99999 00000</li>
             </ul>
          </div>
        </div>
        <div className="max-w-[90rem] mx-auto px-6 lg:px-12 pt-8 border-t border-zinc-900 text-center flex flex-col md:flex-row justify-between items-center text-zinc-600 text-xs tracking-widest uppercase">
          <p>© {new Date().getFullYear()} Apex Elite Club. Colorful Luxury Demo.</p>
          <p className="mt-4 md:mt-0">Crafted for Excellence.</p>
        </div>
      </footer>
    </div>
  );
};

// ==========================================
// 1. HOME VIEW (Light Dark & Super Colorful)
// ==========================================
// ==========================================
// 1. HOME VIEW (Light Dark & Super Colorful)
// ==========================================
const HomeView = ({ setPage }: { setPage: (page: string) => void }) => {
  const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 1, ease: "easeOut" as const } } };

  return (
    <div className="-mt-24">
      {/* Huge Vibrant Hero - Fixed Button Cut Issue (min-h-screen & pb-24) */}
      <section className="relative min-h-screen flex flex-col lg:flex-row items-center justify-center overflow-hidden bg-zinc-950 pb-24 pt-32 lg:pt-0">
        <div className="absolute inset-0 z-0">
          {/* VIBRANT Colorful Gym Image */}
          <img src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=2000&auto=format&fit=crop" alt="Gym Background" className="w-full h-full object-cover opacity-60 scale-105 animate-[pulse_20s_ease-in-out_infinite]" />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/80 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent"></div>
        </div>
        
        <div className="relative z-10 w-full max-w-[90rem] mx-auto px-6 lg:px-12 flex flex-col items-start pt-12 lg:pt-32">
          <motion.div initial={{ width: 0 }} animate={{ width: 80 }} transition={{ duration: 1 }} className="h-1 bg-gradient-to-r from-amber-500 to-amber-300 mb-8 rounded-full"></motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-amber-500 tracking-[0.4em] text-[10px] md:text-xs font-bold uppercase mb-6 bg-amber-500/10 px-4 py-2 rounded-md border border-amber-500/20">Unrivaled Excellence</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="text-5xl md:text-8xl lg:text-9xl font-serif text-white tracking-tighter mb-8 leading-[1] -ml-1">
            Defy <br /> <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Average.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="text-lg md:text-xl text-zinc-300 font-light max-w-xl mb-12 border-l-2 border-amber-500/50 pl-6 leading-relaxed">
            An invitation-only performance club for those who demand the absolute best in training, vibrant energy, and sports science.
          </motion.p>
          <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} onClick={() => setPage('concierge')} className="group flex items-center gap-4 bg-amber-500 text-zinc-950 px-8 py-5 rounded-xl font-black uppercase tracking-[0.2em] text-xs hover:bg-white transition-all duration-300 shadow-[0_10px_30px_rgba(245,158,11,0.3)]">
             Discover Membership <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
          </motion.button>
        </div>
      </section>

      {/* Colorful Floating Feature Section */}
      <section className="py-40 px-6 max-w-[90rem] mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 z-10">
            <h2 className="text-4xl md:text-6xl font-serif text-white mb-8 leading-tight">Mastery is in the <span className="italic text-amber-500">Details.</span></h2>
            <p className="text-zinc-400 font-light text-lg mb-12 leading-relaxed">
              We eliminated the chaos of commercial gyms. Membership is strictly capped at 500, ensuring our biometric-access facility feels like your private, high-energy training sanctuary.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-10 gap-x-6">
               {[
                 { icon: <Shield size={32}/>, title: "Privacy First", desc: "No crowds. Zero waiting for equipment." },
                 { icon: <Wind size={32}/>, title: "Clinical Hygiene", desc: "Hospital-grade HEPA air filtration." },
                 { icon: <Target size={32}/>, title: "Pro Equipment", desc: "Olympic-grade colorful bumper plates." },
                 { icon: <Fingerprint size={32}/>, title: "Smart Access", desc: "Biometric entry & locker systems." }
               ].map((item, i) => (
                 <div key={i} className="bg-zinc-900/50 p-6 rounded-2xl border border-zinc-800 hover:border-amber-500/50 transition-colors">
                   <div className="text-amber-500 mb-4">{item.icon}</div>
                   <h4 className="text-white font-serif text-xl mb-2">{item.title}</h4>
                   <p className="text-zinc-400 text-sm font-light">{item.desc}</p>
                 </div>
               ))}
            </div>
          </div>
          <div className="lg:col-span-7 relative">
             <div className="absolute top-10 -left-10 w-full h-full bg-zinc-900 rounded-3xl border border-zinc-800 -z-10"></div>
             {/* FIXED BROKEN IMAGE HERE */}
             <img src="https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?q=80&w=1200&auto=format&fit=crop" alt="Gym" className="w-full h-[700px] object-cover rounded-3xl shadow-2xl shadow-black/50" />
             
             {/* Floating Stat Card */}
             <div className="absolute -bottom-10 right-10 bg-zinc-950 border border-zinc-800 p-8 rounded-2xl shadow-2xl">
               <p className="text-6xl font-serif text-amber-500 mb-2">500</p>
               <p className="text-white font-bold uppercase tracking-widest text-xs">Members Only limit</p>
             </div>
          </div>
        </div>
      </section>

      {/* Massive Editorial Stats (Vibrant Look) */}
      <section className="py-32 bg-zinc-900 border-y border-zinc-800">
        <div className="max-w-[90rem] mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12 text-center divide-y md:divide-y-0 md:divide-x divide-zinc-800">
          {[
             { num: "25k", label: "Square Feet Space" },
             { num: "01", label: "Attention Ratio" },
             { num: "10+", label: "Master Trainers" },
             { num: "-110°C", label: "Cryotherapy Lab" },
          ].map((stat, i) => (
            <div key={i} className="py-8 md:py-0 group">
               <h3 className="text-6xl md:text-7xl font-serif text-white mb-4 group-hover:text-amber-500 transition-colors">{stat.num}</h3>
               <p className="font-sans text-[11px] text-zinc-400 uppercase tracking-[0.3em] font-black">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
// ==========================================
// 2. THE CLUB VIEW (Expanded About)
// ==========================================
const ClubView = () => (
  <div className="pb-32">
    <div className="max-w-[90rem] mx-auto px-6 pt-20">
      
      <div className="mb-40 border-l-4 border-amber-500 pl-8 max-w-4xl bg-zinc-900/30 p-10 rounded-r-3xl">
        <span className="text-amber-500 tracking-[0.3em] text-[10px] font-bold uppercase mb-6 block">Our Genesis</span>
        <h1 className="text-5xl md:text-8xl font-serif text-white leading-[1.1] mb-10">Rejecting the <span className="italic text-zinc-500">Ordinary.</span></h1>
        <p className="text-zinc-300 text-xl font-light leading-relaxed">
          Apex Elite was conceived to bridge the gap between clinical sports science, professional bodybuilding, and five-star hospitality. A facility where excuses go to die, and true physical transformation is engineered in high-definition.
        </p>
      </div>

      {/* Asymmetrical Colorful Image Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-40">
        <div className="lg:col-span-5 mt-0 lg:mt-32">
           <img src="https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?q=80&w=800&auto=format&fit=crop" alt="Details" className="w-full h-[600px] object-cover rounded-3xl shadow-xl" />
        </div>
        <div className="lg:col-span-7">
           <img src="https://images.unsplash.com/photo-1574680096145-d05b474e2155?q=80&w=1200&auto=format&fit=crop" alt="Equipment" className="w-full h-[700px] object-cover rounded-3xl shadow-2xl" />
           <div className="bg-zinc-950 border border-zinc-800 p-12 rounded-3xl -mt-24 ml-0 lg:-ml-20 relative z-10 max-w-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
             <h3 className="text-3xl font-serif text-white mb-6">Engineered for Results.</h3>
             <p className="text-zinc-400 font-light leading-relaxed text-lg">
               We imported the finest biomechanically precise equipment globally. Every machine is placed to optimize flow. The vibrant lighting is designed to boost adrenaline and highlight muscle definition. The air is infused with subtle eucalyptus to invigorate the senses.
             </p>
           </div>
        </div>
      </div>
      
      {/* Vibrant Master Trainers */}
      <div className="text-center mb-20 border-t border-zinc-900 pt-32">
        <h2 className="text-4xl md:text-6xl font-serif text-white mb-4">The <span className="italic text-amber-500">Masters</span></h2>
        <p className="text-zinc-500 font-light uppercase tracking-widest text-xs">Our Global Expert Panel</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          { name: "Dr. Aryan Sen", role: "Head of Sports Science", img: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?q=80&w=600&auto=format&fit=crop" },
          { name: "Elena Rostova", role: "Lead Recovery Specialist", img: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?q=80&w=600&auto=format&fit=crop" },
          { name: "Marcus Thorne", role: "Strength & Conditioning", img: "https://images.unsplash.com/photo-1583465573242-6e271e54c86b?q=80&w=600&auto=format&fit=crop" }
        ].map((trainer, idx) => (
          <div key={idx} className="group bg-zinc-900 p-6 rounded-3xl border border-zinc-800 hover:border-amber-500 transition-all duration-500">
            <div className="overflow-hidden mb-6 h-[400px] rounded-xl">
              {/* Colorful vibrant images */}
              <img src={trainer.img} alt={trainer.name} className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700" />
            </div>
            <h3 className="text-2xl font-serif text-white mb-2">{trainer.name}</h3>
            <p className="text-amber-500 uppercase tracking-widest text-xs font-bold">{trainer.role}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ==========================================
// 3. DISCIPLINES VIEW (Programs / Services)
// ==========================================
const DisciplinesView = ({ setPage }: { setPage: (page: string) => void }) => (
  <div className="pb-32">
    <div className="max-w-[90rem] mx-auto px-6 pt-20">
      
      <div className="text-center mb-32 bg-zinc-900 py-20 rounded-3xl border border-zinc-800">
        <h1 className="text-5xl md:text-8xl font-serif text-white mb-6">The <span className="text-amber-500 italic">Architecture</span></h1>
        <p className="text-zinc-400 font-light max-w-2xl mx-auto text-xl">We do not offer standard classes. We offer highly specialized, vibrant disciplines led by masters of their craft.</p>
      </div>

      {/* Staggered Vertical Layout with Colorful Images */}
      <div className="space-y-40">
        {[
          { num: "01", title: "Private Coaching", desc: "Clinical body assessments leading to bespoke training architecture designed exclusively for your physiology.", img: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=80&w=1000&auto=format&fit=crop" },
          { num: "02", title: "Sports Science", desc: "Eliminate guesswork with VO2 Max testing, DEXA scans, and real-time biometric tracking displayed on vibrant LED screens.", img: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=1000&auto=format&fit=crop" },
          { num: "03", title: "Combat & Flow", desc: "A custom boxing ring, vibrant green turf zone, and clinical Pilates studio for functional power and agility.", img: "https://images.unsplash.com/photo-1549476464-37392f717541?q=80&w=1000&auto=format&fit=crop" }
        ].map((block, i) => (
          <div key={i} className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className={`lg:col-span-5 ${i % 2 !== 0 ? 'lg:order-2 lg:pl-16' : 'lg:order-1'}`}>
               <span className="text-[120px] font-serif text-zinc-900 leading-none block -mb-10">{block.num}</span>
               <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 relative z-10">{block.title}</h2>
               <p className="text-zinc-400 font-light mb-10 text-lg leading-relaxed relative z-10">{block.desc}</p>
               <button onClick={() => setPage('concierge')} className="flex items-center gap-4 bg-amber-500/10 text-amber-500 px-6 py-3 rounded-full text-xs tracking-[0.2em] font-bold uppercase hover:bg-amber-500 hover:text-zinc-950 transition-colors">
                 Inquire Now <ArrowRight size={14}/>
               </button>
            </div>
            <div className={`lg:col-span-7 ${i % 2 !== 0 ? 'lg:order-1' : 'lg:order-2'}`}>
              <div className="relative overflow-hidden rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-zinc-800">
                {/* Full color images */}
                <img src={block.img} alt={block.title} className="w-full h-[600px] object-cover hover:scale-105 transition-all duration-1000" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ==========================================
// 4. RECOVERY VIEW (Sanctuary & Spa)
// ==========================================
const RecoveryView = ({ setPage }: { setPage: (page: string) => void }) => (
  <div className="pb-32">
    {/* Full width vibrant hero for Spa */}
    <div className="relative h-[80vh] mb-32 flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874?q=80&w=2000&auto=format&fit=crop" alt="Spa" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/80 to-transparent"></div>
      </div>
      <div className="relative z-10 px-6 lg:px-12 max-w-[90rem] mx-auto w-full">
        <span className="bg-amber-500 text-zinc-950 px-4 py-1 rounded-md tracking-[0.3em] text-[10px] font-bold uppercase mb-6 inline-block">Regeneration</span>
        <h1 className="text-6xl md:text-8xl font-serif text-white max-w-3xl leading-tight">The Wellness <span className="italic text-amber-500">Suite.</span></h1>
        <p className="text-zinc-300 font-light mt-6 text-xl max-w-xl">Accelerate muscle repair and calm the nervous system in our high-tech recovery zone.</p>
      </div>
    </div>

    <div className="max-w-[90rem] mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {[
          { icon: <Droplet size={36} />, title: "Cold Plunge Therapy", desc: "Reduce inflammation and stimulate the vagus nerve in our 4°C plunge pools." },
          { icon: <Activity size={36} />, title: "Whole-Body Cryo", desc: "3-minute sessions at -110°C to accelerate muscle repair and flush toxins." },
          { icon: <Fingerprint size={36} />, title: "Infrared Saunas", desc: "Deep cellular detox in private, eucalyptus-infused cedar cabins." },
          { icon: <Plus size={36} />, title: "IV Vitamin Lounge", desc: "Custom hydration drips administered by licensed medical professionals." }
        ].map((item, i) => (
          <div key={i} className="bg-zinc-900 border border-zinc-800 p-8 rounded-3xl hover:border-amber-500 transition-colors group">
            <div className="w-16 h-16 bg-zinc-950 rounded-full flex items-center justify-center text-amber-500 mb-6 group-hover:scale-110 transition-transform">{item.icon}</div>
            <h3 className="text-2xl font-serif text-white mb-4">{item.title}</h3>
            <p className="text-zinc-400 text-sm font-light leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ==========================================
// 5. MEMBERSHIPS VIEW (Expanded & Vibrant)
// ==========================================
const MembershipsView = ({ setPage }: { setPage: (page: string) => void }) => (
  <div className="pb-32">
    <div className="max-w-[90rem] mx-auto px-6 pt-20">
      
      <div className="text-center mb-24">
        <h1 className="text-5xl md:text-8xl font-serif text-white mb-6">Exclusive <span className="text-amber-500 italic">Tiers</span></h1>
        <p className="text-zinc-400 font-light max-w-2xl mx-auto text-xl">Membership is strictly capped and by application only.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center mb-32">
        
        {/* Tier 1 */}
        <div className="bg-zinc-900 p-10 rounded-3xl border border-zinc-800 hover:border-zinc-700 transition-all h-full flex flex-col">
          <h3 className="text-zinc-400 tracking-[0.2em] text-[10px] uppercase font-bold mb-4 bg-zinc-950 inline-block px-3 py-1 rounded-md w-fit">Elite</h3>
          <div className="text-5xl font-serif text-white mb-8">₹1,50,000<span className="text-sm text-zinc-600 font-sans tracking-normal">/yr</span></div>
          <ul className="space-y-6 mb-12 flex-grow">
            {["24/7 Biometric Access", "Unlimited Masterclasses", "4 Hrs/Mo Wellness Suite", "Quarterly DEXA Scans", "Valet Parking Priority"].map((f, i) => (
              <li key={i} className="flex items-center gap-4 text-sm text-zinc-300 font-light border-b border-zinc-800 pb-4"><CheckCircle size={18} className="text-amber-500"/> {f}</li>
            ))}
          </ul>
          <button onClick={() => setPage('concierge')} className="w-full py-4 border border-zinc-700 rounded-xl text-white uppercase text-[10px] font-bold tracking-[0.2em] hover:bg-white hover:text-black transition-colors">Apply Now</button>
        </div>

        {/* Tier 2 (Black Card - Colorful Glow Effect) */}
        <div className="bg-zinc-950 p-12 rounded-3xl border-2 border-amber-500 shadow-[0_0_50px_rgba(245,158,11,0.15)] relative z-10 transform lg:scale-105 h-full flex flex-col">
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-600 to-amber-400 text-zinc-950 px-6 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] font-black shadow-lg">By Invitation</div>
          <h3 className="text-amber-500 tracking-[0.2em] text-[10px] uppercase font-bold mb-4 bg-amber-500/10 inline-block px-3 py-1 rounded-md w-fit">The Black Card</h3>
          <div className="text-6xl font-serif text-white mb-8">₹3,00,000<span className="text-sm text-amber-500/50 font-sans tracking-normal">/yr</span></div>
          <ul className="space-y-6 mb-12 flex-grow">
            {["Everything in Elite", "Unlimited Wellness Suite", "2 PT Sessions/Week", "Dedicated VIP Locker", "Executive Lounge Access", "Concierge Medical Support"].map((f, i) => (
              <li key={i} className="flex items-center gap-4 text-sm text-white font-medium border-b border-zinc-800 pb-4"><Crown size={18} className="text-amber-500"/> {f}</li>
            ))}
          </ul>
          <button onClick={() => setPage('concierge')} className="w-full py-5 bg-gradient-to-r from-amber-600 to-amber-400 rounded-xl text-zinc-950 uppercase text-[11px] font-black tracking-[0.2em] hover:opacity-90 transition-opacity shadow-lg">Request Black Card</button>
        </div>

        {/* Tier 3: Corporate */}
        <div className="bg-zinc-900 p-10 rounded-3xl border border-zinc-800 hover:border-zinc-700 transition-all h-full flex flex-col">
          <h3 className="text-zinc-400 tracking-[0.2em] text-[10px] uppercase font-bold mb-4 bg-zinc-950 inline-block px-3 py-1 rounded-md w-fit">Corporate</h3>
          <div className="text-5xl font-serif text-white mb-8">Bespoke</div>
          <p className="text-zinc-400 text-sm font-light mb-8">Tailored wellness packages for executive teams and C-suite professionals.</p>
          <ul className="space-y-6 mb-12 flex-grow">
            {["Group Memberships", "Private Team Classes", "Corporate Billing", "Dedicated Account Manager"].map((f, i) => (
              <li key={i} className="flex items-center gap-4 text-sm text-zinc-300 font-light border-b border-zinc-800 pb-4"><CheckCircle size={18} className="text-zinc-600"/> {f}</li>
            ))}
          </ul>
          <button onClick={() => setPage('concierge')} className="w-full py-4 border border-zinc-700 rounded-xl text-white uppercase text-[10px] font-bold tracking-[0.2em] hover:bg-white hover:text-black transition-colors">Contact Us</button>
        </div>

      </div>
    </div>
  </div>
);

// ==========================================
// 6. CONCIERGE VIEW (Contact/FAQ)
// ==========================================
const ConciergeView = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    { q: "Is there a day-pass available?", a: "To protect the privacy of our members, we do not offer day passes. Access is strictly limited to members." },
    { q: "What is the dress code?", a: "Clean, appropriate athletic wear is required. Outdoor shoes are not permitted on the gym floor." },
    { q: "Can I freeze my membership?", a: "Elite and Black Card members can freeze memberships for up to 30 days per calendar year." }
  ];

  return (
    <div className="pb-32">
      <div className="max-w-[90rem] mx-auto px-6 pt-20 grid grid-cols-1 lg:grid-cols-2 gap-24">
        
        <div>
          <h1 className="text-5xl md:text-8xl font-serif text-white mb-8 leading-tight">The <span className="italic text-amber-500">Concierge</span></h1>
          <p className="text-zinc-400 font-light mb-16 text-xl leading-relaxed">Schedule a private tour, inquire about membership status, or book a spa session. We are at your service.</p>
          
          <div className="space-y-10 mb-20 bg-zinc-900 p-10 rounded-3xl border border-zinc-800">
            <div className="flex items-start gap-6">
              <div className="p-4 bg-zinc-950 rounded-2xl text-amber-500 border border-zinc-800"><MapPin size={24}/></div>
              <div>
                <h4 className="text-white font-serif text-xl mb-2">Location</h4>
                <p className="text-zinc-400 font-light text-lg">Level 4, The Opus Tower<br/>Golf Course Road, City</p>
              </div>
            </div>
            <div className="flex items-start gap-6">
              <div className="p-4 bg-zinc-950 rounded-2xl text-amber-500 border border-zinc-800"><Phone size={24}/></div>
              <div>
                <h4 className="text-white font-serif text-xl mb-2">Direct Contact</h4>
                <p className="text-zinc-400 font-light text-lg">concierge@apexelite.demo<br/>+91 99999 00000</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-3xl font-serif text-white mb-8">FAQ</h3>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-zinc-800 bg-zinc-900 rounded-2xl overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full text-left p-6 flex justify-between items-center text-white font-serif text-lg focus:outline-none">
                    {faq.q}
                    {openFaq === i ? <X size={20} className="text-amber-500"/> : <Plus size={20} className="text-amber-500"/>}
                  </button>
                  {openFaq === i && (
                    <div className="p-6 pt-0 text-zinc-400 font-light text-base leading-relaxed border-t border-zinc-800/50 mt-2 pt-4">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-zinc-900 p-12 lg:p-16 rounded-3xl border border-zinc-800 shadow-2xl relative h-fit sticky top-32">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 blur-[100px] rounded-full pointer-events-none"></div>
          
          <h3 className="text-3xl font-serif text-white mb-10">Submit Application</h3>
          <form className="space-y-10" onSubmit={(e) => { e.preventDefault(); alert('Demo: Application Sent.'); }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div className="relative">
                <input type="text" required className="w-full bg-zinc-950 border border-zinc-800 px-6 py-4 rounded-xl text-white focus:outline-none focus:border-amber-500 transition-colors placeholder-zinc-600" placeholder="First Name" />
              </div>
              <div className="relative">
                <input type="text" required className="w-full bg-zinc-950 border border-zinc-800 px-6 py-4 rounded-xl text-white focus:outline-none focus:border-amber-500 transition-colors placeholder-zinc-600" placeholder="Last Name" />
              </div>
            </div>
            <div className="relative">
              <input type="email" required className="w-full bg-zinc-950 border border-zinc-800 px-6 py-4 rounded-xl text-white focus:outline-none focus:border-amber-500 transition-colors placeholder-zinc-600" placeholder="Email Address" />
            </div>
            <div className="relative">
              <input type="tel" required className="w-full bg-zinc-950 border border-zinc-800 px-6 py-4 rounded-xl text-white focus:outline-none focus:border-amber-500 transition-colors placeholder-zinc-600" placeholder="Contact Number" />
            </div>
            <div className="relative">
              <select className="w-full bg-zinc-950 border border-zinc-800 px-6 py-4 rounded-xl text-zinc-400 focus:outline-none focus:border-amber-500 transition-colors cursor-pointer appearance-none">
                <option>Elite Membership Inquiry</option>
                <option>Black Card Application</option>
                <option>Private Club Tour</option>
              </select>
            </div>
            <button type="submit" className="w-full bg-amber-500 text-zinc-950 uppercase font-black text-xs tracking-[0.2em] py-6 rounded-xl hover:bg-white transition-all duration-500 shadow-[0_10px_30px_rgba(245,158,11,0.2)]">
              Send to Concierge
            </button>
            <p className="text-center text-zinc-500 text-[10px] uppercase tracking-widest mt-6">Handled with strict confidentiality.</p>
          </form>
        </div>

      </div>
    </div>
  );
};

export default PremiumGymLanding;